import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { DEVICES, DOOR_TYPES, EXTRAS } from "@/lib/constants";

const num = (v) => (v === "" || v == null ? null : Number(v));
const PriceInput = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-1.5" dir="ltr">
    <Input type="number" min="0" step="any" inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="غير محدد"
      className="h-10 w-28 text-center font-mono placeholder:text-xs placeholder:font-sans" data-testid={testId} />
    <span className="text-xs text-slate-500">ر.س</span>
  </div>
);

const Card = ({ title, hint, children, testId }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
    <p className="font-heading font-bold text-lg">{title}</p>
    {hint && <p className="text-xs text-slate-500 mb-3">{hint}</p>}
    <div className="divide-y divide-slate-100">{children}</div>
  </section>
);

const Line = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <span className="font-semibold text-sm" dir="auto">{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

export default function Prices() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/prices").then((r) => setP({ doors: {}, devices: {}, extras: {}, install: null, ...r.data })).catch((e) => toast.error(errMsg(e)));
  }, []);

  const setDoor = (name, k, v) => setP((x) => ({ ...x, doors: { ...x.doors, [name]: { unit: "door", ...x.doors[name], [k]: v } } }));
  const setIn = (group, name, v) => setP((x) => ({ ...x, [group]: { ...x[group], [name]: v } }));

  const save = async () => {
    const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
    const doors = Object.fromEntries(Object.entries(p.doors).filter(([, d]) => d.price !== "" && d.price != null).map(([k, d]) => [k, { price: Number(d.price), unit: d.unit || "door" }]));
    setBusy(true);
    try {
      const { data } = await api.put("/prices", { doors, devices: clean(p.devices), extras: clean(p.extras), install: num(p.install) });
      setP(data);
      toast.success("تم حفظ قائمة الأسعار");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!p) return <div className="grid place-items-center h-64"><Loader2 className="animate-spin text-brand-red" /></div>;

  return (
    <div className="space-y-6 pb-24" data-testid="prices-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold text-brand-navy mb-1 flex items-center gap-1.5"><Tag size={14} />PRICES</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">قائمة الأسعار</h1>
          <p className="text-sm text-slate-500 mt-1">الأسعار قبل الضريبة، وتُعبّأ تلقائياً في عرض السعر ويمكن تعديلها قبل الإرسال.</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="أنواع الأبواب" hint="اختر هل السعر للباب الواحد أم للمتر المربع (يُحسب من العرض × الارتفاع × عدد الأبواب)." testId="prices-doors">
          {DOOR_TYPES.map((d, i) => (
            <Line key={d} label={d}>
              <select value={p.doors[d]?.unit || "door"} onChange={(e) => setDoor(d, "unit", e.target.value)} data-testid={`price-door-unit-${i}`}
                className="h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white">
                <option value="door">للباب</option>
                <option value="m2">للمتر²</option>
              </select>
              <PriceInput value={p.doors[d]?.price} onChange={(v) => setDoor(d, "price", v)} testId={`price-door-${i}`} />
            </Line>
          ))}
        </Card>
        <div className="space-y-5">
          <Card title="الإضافات والتركيب" testId="prices-extras">
            {EXTRAS.map((x, i) => <Line key={x} label={x}><PriceInput value={p.extras[x]} onChange={(v) => setIn("extras", x, v)} testId={`price-extra-${i}`} /></Line>)}
            <Line label="التركيب والتوصيل"><PriceInput value={p.install} onChange={(v) => setP((s) => ({ ...s, install: v }))} testId="price-install" /></Line>
          </Card>
          <Card title="الأجهزة (الماتورات)" hint="سعر الجهاز الواحد." testId="prices-devices">
            {DEVICES.map((x, i) => <Line key={x} label={x}><PriceInput value={p.devices[x]} onChange={(v) => setIn("devices", x, v)} testId={`price-device-${i}`} /></Line>)}
          </Card>
        </div>
      </div>
      <div className="fixed bottom-0 inset-x-0 lg:right-64 bg-white/90 backdrop-blur border-t border-slate-200 p-3 flex justify-end">
        <button onClick={save} disabled={busy} data-testid="prices-save-button"
          className="h-12 px-6 rounded-lg bg-brand-navy text-white font-bold flex items-center gap-2 hover:opacity-90 disabled:opacity-60 transition-opacity">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}حفظ الأسعار
        </button>
      </div>
    </div>
  );
}
